import {
  Badge,
  BrandLoader,
  Card,
  Flex,
  FormatDeliveryDate,
  Icon,
  IconButton,
  OverlayTrigger,
  Popover,
  Text,
  Tooltip,
} from '@wyz/ui';
import {
  DateUtils,
  DEFAULT_TIRE_BRAND_LOGO,
  DEFAULT_TIRE_BRAND_WIDTH,
  getBrandLogoUrl,
} from '@wyz/utils';
import clsx from 'clsx';
import { PropsWithChildren } from 'react';
import { Trans, useConfig, useLocale } from '~/common/context';
import { Img, SeasonIcon, ValueInfoItem } from '~/purchase/components';
import { WcwCartHelpers, WcwSearchHelpers } from '~/purchase/helpers';
import { IWcwCartItem, IWcwCartItemErrors } from '~/purchase/types';

function ErrorIndicator({ children }: PropsWithChildren) {
  return (
    <OverlayTrigger
      trigger={['focus', 'hover']}
      placement='bottom'
      overlay={<Popover className='py-1 px-2'>{children}</Popover>}
    >
      <div>
        <Icon icon='warning-triangle' size='xxs' />
      </div>
    </OverlayTrigger>
  );
}

type Props = {
  item: IWcwCartItem;
  onRemove: (id: string) => void;
  isRemoving?: boolean;
  errors?: IWcwCartItemErrors;
};

function WcwCartRow({ item, onRemove, isRemoving, errors }: Props) {
  const locale = useLocale();
  const { cdnUrl } = useConfig();

  const priceChanged = errors?.ERR_PRICE_CHANGED !== undefined;
  const dateChanged = errors?.ERR_DELIVERY_DATE_CHANGED !== undefined;
  const outOfStock =
    errors?.ERR_QUANTITY_0 === true ||
    WcwCartHelpers.isTechnicalError(errors ?? {});

  return (
    <Card
      className={clsx({
        'border border-2 border-warning-text': priceChanged || dateChanged,
        'border border-2 border-danger': outOfStock,
      })}
    >
      <div className='bg-neutral-grey-1 p-4 rounded-top border-bottom border-neutral-gray-4'>
        <Flex justifyContent='between' wrap='wrap'>
          <Flex alignItems='start' className='gap-3'>
            <Flex direction='column' alignItems='start' className='gap-1'>
              <Img
                src={item.wheel.image}
                alt={item.wheel.name}
                width={90}
                height={90}
                className='object-fit-contain flex-shrink-0'
                baseUrl={cdnUrl}
                defaultImage={WcwSearchHelpers.DEFAULT_WHEEL_IMAGE}
              />
              <Tooltip label={item.tyre.brand}>
                <Img
                  src={getBrandLogoUrl(item.tyre.brand)}
                  baseUrl={cdnUrl}
                  defaultImage={DEFAULT_TIRE_BRAND_LOGO}
                  alt={item.tyre.brand}
                  width={DEFAULT_TIRE_BRAND_WIDTH.sm}
                  loader={BrandLoader}
                  className='mt-1'
                />
              </Tooltip>
            </Flex>
            <div>
              <Text
                weight='bold'
                className='text-uppercase'
                heading='sm'
                color='tertiary'
              >
                {item.wheel.name}
              </Text>
              <Flex alignItems='center' className='gap-1 mt-1'>
                <Badge pill bg='info' className='fs-xxs fw-semi-bold'>
                  {item.tyre.dimension}
                </Badge>
                <SeasonIcon value={item.tyre.season} size='xs' />
              </Flex>
              <Text heading='xxs' color='tertiary' className='mt-1'>
                {item.tyre.profile}
              </Text>
              <Text heading='xxs' color='secondary-gray'>
                <Trans
                  i18nKey='common.tyre.ref_value'
                  values={{ value: item.wheel.reference }}
                />
              </Text>
              {outOfStock && (
                <Text heading='xxs' color='danger' weight='semi-bold'>
                  <Trans i18nKey='app.purchase.cart_page.unavailable_modal.unavailable_product' />
                </Text>
              )}
            </div>
          </Flex>
          <Flex alignItems='center' className='gap-2'>
            <div>
              <Flex alignItems='center' className='gap-1'>
                <ValueInfoItem
                  as='div'
                  title={<Trans i18nKey='app.common.quantity' />}
                >
                  <Text heading='normal' weight='bold' color='primary'>
                    <Trans
                      i18nKey='app.purchase.wcw.search_page.set_option'
                      values={{ count: item.quantity }}
                    />
                  </Text>
                </ValueInfoItem>
                <IconButton
                  id={`wcw-remove-${item.id}`}
                  variant='link'
                  icon='delete-bin-line'
                  iconSize='sm'
                  iconAlign='middle'
                  onClick={() => onRemove(item.id)}
                  isLoading={isRemoving}
                />
              </Flex>
            </div>
          </Flex>
        </Flex>
      </div>
      <Flex className='p-4 w-100' justifyContent='between' wrap='wrap'>
        <ValueInfoItem
          as='div'
          title={<Trans i18nKey='app.purchase.cart_page.table.source' />}
        >
          <Text heading='normal' weight='bold' color='primary'>
            {item.supplier.name}
          </Text>
        </ValueInfoItem>
        <ValueInfoItem
          as='div'
          title={
            <Trans i18nKey='app.purchase.wcw.result_page.recommended_price' />
          }
        >
          <Text heading='normal' weight='semi-bold' color='primary'>
            {locale.formatters.money(item.recommendedPrice, {
              currency: item.currency,
            })}
          </Text>
        </ValueInfoItem>
        <ValueInfoItem
          as='div'
          title={<Trans i18nKey='app.common.estimation_delivery' />}
        >
          {dateChanged ? (
            <Badge bg='warning'>
              <DeliveryValue item={item} errors={errors} />
            </Badge>
          ) : (
            <DeliveryValue item={item} errors={errors} />
          )}
        </ValueInfoItem>
        <ValueInfoItem
          as='div'
          title={<Trans i18nKey='app.purchase.cart_page.table.unit_price' />}
          align='right'
        >
          <Flex justifyContent='end'>
            {priceChanged ? (
              <Badge bg='warning'>
                <PriceValue item={item} errors={errors} />
              </Badge>
            ) : (
              <PriceValue item={item} errors={errors} />
            )}
          </Flex>
        </ValueInfoItem>
        <ValueInfoItem
          as='div'
          title={<Trans i18nKey='app.common.total_price_without_tax' />}
          align='right'
        >
          <Flex justifyContent='end' alignItems='center'>
            <Text heading='normal' weight='bold' color='primary'>
              {locale.formatters.money(item.totalExclTax, {
                currency: item.currency,
              })}
            </Text>
          </Flex>
        </ValueInfoItem>
      </Flex>
    </Card>
  );
}

function PriceValue({ item, errors }: Pick<Props, 'item' | 'errors'>) {
  const locale = useLocale();
  const oldPrice = errors?.ERR_PRICE_CHANGED;

  return (
    <Flex
      alignItems='center'
      justifyContent='end'
      className='gap-1 position-relative'
    >
      {oldPrice !== undefined && (
        <ErrorIndicator>
          <Trans
            i18nKey='app.purchase.cart.errors.invalid_price'
            values={{
              price: locale.formatters.money(oldPrice, {
                currency: item.currency,
              }),
            }}
            components={[
              <Text
                key={0}
                heading='xs'
                color='secondary'
                weight='semi-bold'
                component='div'
              />,
              <Text
                key={1}
                heading='xs'
                color='success'
                weight='semi-bold'
                component='div'
              />,
            ]}
          />
        </ErrorIndicator>
      )}
      <Text
        heading='normal'
        weight='bold'
        color={oldPrice !== undefined ? 'warning' : 'primary'}
      >
        {locale.formatters.money(item.unitPriceExclTax, {
          currency: item.currency,
        })}
      </Text>
    </Flex>
  );
}

function DeliveryValue({ item, errors }: Pick<Props, 'item' | 'errors'>) {
  const locale = useLocale();
  const oldDate = errors?.ERR_DELIVERY_DATE_CHANGED;

  return (
    <Flex alignItems='center' className='gap-1 position-relative'>
      <Text
        heading='normal'
        weight='semi-bold'
        color={oldDate !== undefined ? 'warning' : 'primary'}
      >
        <FormatDeliveryDate
          orderType={null}
          backorderContent={<Trans i18nKey='app.common.offer_type.backorder' />}
          date={item.estimatedDelivery}
          format={DateUtils.DATE_FORMAT.DM}
          locale={locale.locale}
        />
      </Text>
      {oldDate !== undefined && (
        <ErrorIndicator>
          <Trans
            i18nKey='app.purchase.cart.errors.invalid_date'
            values={{
              date: locale.formatters.date(new Date(oldDate), {
                format: DateUtils.DATE_FORMAT.DM,
              }),
            }}
            components={[
              <Text
                key={0}
                heading='xs'
                color='secondary'
                weight='semi-bold'
                component='div'
              />,
              <Text
                key={1}
                heading='xs'
                color='success'
                weight='semi-bold'
                component='div'
              />,
            ]}
          />
        </ErrorIndicator>
      )}
    </Flex>
  );
}

export default WcwCartRow;
